import { useId } from 'react';
import { COMMENT_EMOJI } from './emojiSet';
import { usePopover } from './CommentInsertPickers';
import { AnimatedEmoji } from './AnimatedEmoji';
import { NOTO_CREDIT } from './notoEmoji';
import type { ReactionSummary } from './reactions';

/**
 * The quick reactions row under a comment (Messaging Drop 3b-2). ONE
 * component, used by the Messages page (MessagesPage.tsx) and by every task
 * and project comment thread (CommentsSection.tsx).
 *
 * Existing reactions show as chips with a count (reactions are public within
 * the room, like the thumbs on agent messages). Clicking a chip adds yours or
 * takes yours back.
 * "Add a reaction" opens the same emoji set the comment box uses
 * (emojiSet.ts), a short quick line first.
 *
 * The chips and the quick line animate (AnimatedEmoji.tsx) when the emoji has
 * a Noto file (notoEmoji.ts): today the eight QUICK_REACTIONS. The full grid
 * and any other emoji stay plain text. The quick line's title carries the
 * CC BY 4.0 credit for the animations.
 *
 * A stored emoji is only ever React text. The database accepts any short
 * text without spaces or angle brackets (findings-dev4-drop3b2-db.md item 7),
 * so it never goes into HTML or into an attribute built as a string.
 *
 * canReact is a convenience that hides the controls for people the database
 * refuses (TAP Viewers); RLS on comment_reactions is the real guard.
 *
 * The Console chat (ConsoleConversation.tsx) passes three optional props:
 * describe names who reacted on each chip, onAdd opens its own message sheet
 * instead of this popover (a bottom sheet works on a phone and inside a
 * scrolling chat), and addLabel names the add button for that message.
 */

/** The short line at the top of the picker. Every entry is in COMMENT_EMOJI. */
export const QUICK_REACTIONS = ['👍', '❤️', '😂', '🎉', '👀', '🙏', '✅', '🔥'] as const;

const EMOJI_NAMES = new Map(COMMENT_EMOJI.map((entry) => [entry.char, entry.name]));

export function reactionName(emoji: string): string {
  return EMOJI_NAMES.get(emoji) ?? 'reaction';
}

function chipLabel(reaction: ReactionSummary): string {
  const people = reaction.count === 1 ? '1 person' : `${reaction.count} people`;
  return `${reactionName(reaction.emoji)}, ${people}${reaction.mine ? ', including you' : ''}`;
}

export function CommentReactions({
  reactions,
  canReact,
  pending = false,
  onToggle,
  onAdd,
  addLabel = 'Add a reaction',
  describe,
}: {
  reactions: ReactionSummary[] | undefined;
  canReact: boolean;
  pending?: boolean;
  onToggle: (emoji: string, mine: boolean) => void;
  onAdd?: () => void;
  addLabel?: string;
  describe?: (reaction: ReactionSummary) => string;
}) {
  const { open, setOpen, wrapRef } = usePopover();
  const panelId = useId();
  const list = reactions ?? [];

  if (list.length === 0 && !canReact) {
    return null;
  }

  function pick(emoji: string) {
    setOpen(false);
    const existing = list.find((entry) => entry.emoji === emoji);
    // Picking one you already added from the grid is not a second vote.
    if (existing?.mine === true) {
      return;
    }
    onToggle(emoji, false);
  }

  return (
    <div className="comment-reactions" role="group" aria-label="Reactions">
      {list.map((reaction) =>
        canReact ? (
          <button
            key={reaction.emoji}
            type="button"
            className={`comment-reaction${reaction.mine ? ' comment-reaction--mine' : ''}`}
            aria-label={describe ? describe(reaction) : chipLabel(reaction)}
            aria-pressed={reaction.mine}
            title={describe ? describe(reaction) : reactionName(reaction.emoji)}
            disabled={pending}
            onClick={() => onToggle(reaction.emoji, reaction.mine)}
          >
            <span aria-hidden="true">
              <AnimatedEmoji emoji={reaction.emoji} />
            </span>
            <span className="comment-reaction-count">{reaction.count}</span>
          </button>
        ) : (
          <span
            key={reaction.emoji}
            className="comment-reaction"
            aria-label={describe ? describe(reaction) : chipLabel(reaction)}
          >
            <span aria-hidden="true">
              <AnimatedEmoji emoji={reaction.emoji} />
            </span>
            <span className="comment-reaction-count">{reaction.count}</span>
          </span>
        ),
      )}
      {canReact && onAdd ? (
        <button
          type="button"
          className="comment-reaction comment-reaction-add"
          aria-label={addLabel}
          aria-haspopup="dialog"
          disabled={pending}
          onClick={onAdd}
        >
          <span aria-hidden="true">🙂+</span>
        </button>
      ) : canReact ? (
        <span className="comment-picker-wrap" ref={wrapRef}>
          <button
            type="button"
            className="comment-reaction comment-reaction-add"
            aria-label={addLabel}
            aria-haspopup="dialog"
            aria-expanded={open}
            aria-controls={open ? panelId : undefined}
            disabled={pending}
            onClick={() => setOpen((was) => !was)}
          >
            <span aria-hidden="true">🙂+</span>
          </button>
          {open ? (
            <div
              id={panelId}
              className="comment-picker comment-emoji-picker comment-reaction-picker"
              role="dialog"
              aria-label="Pick a reaction"
            >
              <div className="comment-reaction-quick" title={NOTO_CREDIT}>
                {QUICK_REACTIONS.map((emoji) => (
                  <button
                    key={emoji}
                    type="button"
                    className="comment-emoji"
                    aria-label={`React ${reactionName(emoji)}`}
                    title={reactionName(emoji)}
                    onClick={() => pick(emoji)}
                  >
                    <AnimatedEmoji emoji={emoji} />
                  </button>
                ))}
              </div>
              <div className="comment-emoji-grid">
                {COMMENT_EMOJI.map((emoji) => (
                  <button
                    key={emoji.char}
                    type="button"
                    className="comment-emoji"
                    aria-label={emoji.name}
                    title={emoji.name}
                    onClick={() => pick(emoji.char)}
                  >
                    {emoji.char}
                  </button>
                ))}
              </div>
            </div>
          ) : null}
        </span>
      ) : null}
    </div>
  );
}
